import cx from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import {
  boardSwitcherSearchSharedState,
  boardSwitcherSharedState,
} from '@trello/board-switcher';
import { useFeatureGate } from '@trello/feature-gate-client';
import { getScreenFromUrl } from '@trello/marketing-screens';
import { useSharedStateSelector } from '@trello/shared-state';
import { useSplitScreenSharedState } from '@trello/split-screen';

import { BoardSwitcherHeader } from './BoardSwitcherHeader';
import { BoardSwitcherSpotlight } from './BoardSwitcherSpotlight';
import { RecentBoards } from './RecentBoards';
import { SearchResults } from './SearchResults';
import { SelectedWorkspace } from './SelectedWorkspace';
import { StarredBoards } from './StarredBoards';
import { useBoardSwitcher } from './useBoardSwitcher';
import { WorkspaceBoards } from './WorkspaceBoards';
import { WorkspaceTabs } from './WorkspaceTabs';

import * as styles from './BoardSwitcher.module.less';

interface BoardSwitcherProps {
  isPanel?: boolean;
}

export const BoardSwitcher: FunctionComponent<BoardSwitcherProps> = ({
  isPanel = false,
}) => {
  const { value: isBoardSwitcherPanelEnabled } = useFeatureGate(
    'billplat_board_switcher_panel',
  );
  const { guestWorkspaceIds, sortedWorkspaceIds } = useBoardSwitcher();

  const selectedWorkspaceId = useSharedStateSelector(
    boardSwitcherSharedState,
    useCallback((state) => state.selectedWorkspaceId, []),
  );

  const { areMultiplePanelsOpen, configuration, panels } =
    useSplitScreenSharedState();

  const isSinglePanel =
    (isBoardSwitcherPanelEnabled && configuration === 'tabs') ||
    (isPanel && !areMultiplePanelsOpen);

  const query = useSharedStateSelector(
    boardSwitcherSearchSharedState,
    useCallback((value) => value.query, []),
  );

  useEffect(() => {
    if (!isPanel) {
      return;
    }
    if (panels.switcher) {
      Analytics.sendViewedComponentEvent({
        componentName: 'boardSwitcherPanel',
        componentType: 'panel',
        source: getScreenFromUrl(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panels.switcher]);

  return (
    <BoardSwitcherSpotlight>
      <div
        className={cx({
          [styles.boardSwitcher]: true,
          [styles.panel]: isPanel,
          [styles.singlePanel]: isSinglePanel,
        })}
        id="board-switcher"
      >
        <BoardSwitcherHeader isPanel={isPanel} />
        <WorkspaceTabs isPanel={isPanel} />
        {query && (
          <div className={styles.scrollContainer}>
            <SearchResults />
          </div>
        )}

        {!query && (
          <>
            {selectedWorkspaceId ? (
              <SelectedWorkspace
                workspaceId={selectedWorkspaceId}
                isPanel={isPanel}
              />
            ) : (
              <div className={styles.scrollContainer}>
                <StarredBoards />
                <RecentBoards />

                {sortedWorkspaceIds.map((id) => (
                  <WorkspaceBoards
                    key={id}
                    workspaceId={id}
                    showActions={!guestWorkspaceIds.includes(id)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </BoardSwitcherSpotlight>
  );
};
